import { Link } from '@mui/material';
import { GridColDef, ValueOptions } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentEditCellComponent } from '~/components/SolaForceDataGrid';
import {
  UseDataGridCRUDValidationOutput,
  UseDataGridRowFileChangesState,
  useDataGridCRUDColumns,
} from '~/components/SolaForceDataGrid/hooks';
import { conditionalGridColDefs } from '~/components/SolaForceDataGrid/utils';
import {
  CompanyFormsEntry,
  EmployeeFormsEntry,
} from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/data';
import { getEmployeeAttachmentUrl } from '~/utils/helper';

export type UseFormsTableColumnsProps = {
  selectedEmployeeId: number;
  companyFormsEntries: CompanyFormsEntry[];
  canEdit: boolean;
} & Pick<
  UseDataGridCRUDValidationOutput<EmployeeFormsEntry>,
  'columnsValidation'
> &
  Pick<UseDataGridRowFileChangesState, 'updateFileChanges' | 'fileChanges'>;

type UseFormsTableColumnsOutput = {
  columns: GridColDef<EmployeeFormsEntry>[];
};

export const useFormsTableColumns = (
  props: UseFormsTableColumnsProps,
): UseFormsTableColumnsOutput => {
  const {
    selectedEmployeeId,
    companyFormsEntries,
    columnsValidation,
    updateFileChanges,
    fileChanges,
    canEdit,
  } = props;

  const { t } = useTranslation();

  const columns: GridColDef<EmployeeFormsEntry>[] = useMemo(() => {
    const dateColumnSizeProps = {
      flex: 0.3,
      minWidth: 130,
    };

    return [
      {
        field: 'companyFormId',
        headerName: t('laFormName'),
        type: 'singleSelect',
        editable: true,
        flex: 1,
        minWidth: 160,
        valueGetter: ({ value }) => (value < 0 ? '' : value),
        valueOptions: companyFormsEntries.map(
          (e): ValueOptions => ({
            value: e.id,
            label: e.name,
          }),
        ),
        valueFormatter: ({ value }) => {
          return (
            companyFormsEntries.find((e) => e.id === value)?.name ?? value ?? ''
          );
        },
      },
      {
        field: 'created',
        headerName: t('laCreatedTime'),
        type: 'date',
        ...dateColumnSizeProps,
      },
      {
        field: 'modified',
        headerName: t('laModifiedTime'),
        type: 'date',
        ...dateColumnSizeProps,
      },
      {
        field: 'attachmentName',
        headerName: t('laDocumentName'),
        editable: true,
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ value, row }) => {
          return (
            <Link
              href={getEmployeeAttachmentUrl(
                selectedEmployeeId,
                row.attachmentId,
              )}
              target={'_blank'}
            >
              {value}
            </Link>
          );
        },
        renderEditCell: (params) => {
          return (
            <AttachmentEditCellComponent
              params={params}
              required={true}
              onCellFileChange={({ value, params, apiRef }) => {
                updateFileChanges({ id: params.id, file: value });
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: value?.name ?? '',
                });
              }}
              getAttachmentUrl={({ params }) => {
                const { row } = params;
                if (
                  row.attachmentId >= 0 &&
                  !fileChanges.some((fc) => fc.id === row.id)
                ) {
                  return getEmployeeAttachmentUrl(
                    selectedEmployeeId,
                    row.attachmentId,
                  );
                }
                return undefined;
              }}
            />
          );
        },
      },
      {
        field: 'description',
        type: 'string',
        headerName: t('laDescription'),
        editable: true,
        flex: 1.5,
        minWidth: 200,
      },
      ...conditionalGridColDefs<EmployeeFormsEntry>(canEdit, {
        field: 'actions',
        type: 'actions',
        headerName: t('Actions'),
        width: 90,
        cellClassName: 'actions',
      }),
    ];
  }, [
    t,
    companyFormsEntries,
    selectedEmployeeId,
    updateFileChanges,
    fileChanges,
    canEdit,
  ]);

  return useDataGridCRUDColumns({
    columns,
    columnsValidation,
  });
};
